// Single point of contact with Zoho Desk: the ASAP help center widget and the single sign-on of the
// help center web portal. The widget itself is loaded once per page load from the Manager's
// public/index.html; this service only talks to it through window.ZohoDeskAsapReady. It is inactive
// outside the Manager (frameweb shares this code) and where VUE_APP_ZOHO_ASAP_ENABLED is not "true".
// Any failure is swallowed: Zoho must never break the app.
// The JWT is handed straight to Zoho: it is never stored, logged nor sent to error tracking.
import axios from "axios";
import conn from "@/flit2go-web-core/services/connection";
import { EventBus } from "@/flit2go-web-core/util/event-bus.js";

const JWT_URL = "/secured/v1/integration/zoho/asap/jwt";
const SSO_JWT_URL = "/secured/v1/integration/zoho/sso/jwt";
// JWT sign-in URL of the help center web portal; return_to must point to this same host
const PORTAL_SIGNIN_URL = process.env.VUE_APP_ZOHO_DESK_PORTAL_SIGNIN_URL;

export const STATUS = {
  IDLE: "idle",
  PENDING: "pending",
  IDENTIFIED: "identified",
  UNAVAILABLE: "unavailable",
};

// Own axios instance on purpose: the shared one redirects to /s/error on 404 or network errors,
// and a missing or failing endpoint must leave the Manager untouched
const tokenClient = axios.create({ baseURL: process.env.VUE_APP_API_URL });

let status = STATUS.IDLE;
// Bumped on every login and logout, so a token requested for a previous user is never handed over
let sessionId = 0;
let listenersBound = false;
let appOpen = false;
let unavailableHandler = null;
// Click on the help center waiting for the widget to load: one at a time, with its own deadline
let pendingOpen = null;
// How long that click waits for the widget before showing the unavailable page
const OPEN_WAIT_MS = 10000;

function isEnabled() {
  return (
    process.env.VUE_APP_TITLE === "Manager" &&
    process.env.VUE_APP_ZOHO_ASAP_ENABLED === "true"
  );
}

function isWidgetReady() {
  return (
    window.ZohoDeskAsapReadyStatus === true &&
    typeof window.ZohoDeskAsap?.invoke === "function"
  );
}

function whenReady(callback) {
  if (!isEnabled() || typeof window.ZohoDeskAsapReady !== "function") return;
  try {
    window.ZohoDeskAsapReady(() => {
      try {
        callback(window.ZohoDeskAsap);
      } catch {
        // Do nothing
      }
    });
  } catch {
    // Do nothing
  }
}

function notifyUnavailable() {
  if (typeof unavailableHandler !== "function") return;
  try {
    unavailableHandler();
  } catch {
    // Do nothing
  }
}

function bindListeners(asap) {
  if (listenersBound) return;
  listenersBound = true;
  asap.on("onAppOpen", () => {
    appOpen = true;
    if (status === STATUS.UNAVAILABLE) {
      asap.invoke("close");
      notifyUnavailable();
    }
  });
  // Closing the app shows the floating icon again, even if it was hidden before opening
  asap.on("onAppClose", () => {
    appOpen = false;
    hideLauncher(asap);
  });
}

// The user may open the help center while the token is still being requested
function closeIfOpen() {
  if (!appOpen) return;
  whenReady((asap) => asap.invoke("close"));
  notifyUnavailable();
}

// The help center is only opened from the "?" menu of the header, never from the floating icon
function hideLauncher(asap) {
  asap.invoke("hide", "app.launcher");
}

// On a direct page load (e.g. Zoho redirecting to the portal login page) the shared axios instance
// may not have the session header yet, so it falls back to the stored session token
function sessionAuthorization() {
  return (
    conn.$axios.defaults.headers.common["Authorization"] ||
    "Bearer " + localStorage.getItem("user-token")
  );
}

function requestToken(url = JWT_URL) {
  return tokenClient
    .get(url, { headers: { Authorization: sessionAuthorization() } })
    .then((response) => response.data.token);
}

// Expired session: left to the Manager's usual flow, same as the shared interceptor
function isExpiredSession(error) {
  if (!error.response || error.response.status !== 401) return false;
  if (error.response.data && error.response.data.error === "invalid_token") {
    EventBus.$emit("invalid_token", true);
  }
  return true;
}

// Only a page of the help center portal is accepted as return_to, never an arbitrary URL
function safePortalReturnTo(returnTo) {
  const portalOrigin = new URL(PORTAL_SIGNIN_URL).origin;
  try {
    const url = new URL(returnTo);
    if (url.origin === portalOrigin) return url.href;
  } catch {
    // Do nothing
  }
  return portalOrigin;
}

// Called by the widget every time it needs to identify the user, so every call asks for a new token
function getJwtToken(successCallback, failureCallback) {
  const requestSessionId = sessionId;
  requestToken()
    .then((token) => {
      if (requestSessionId !== sessionId || !token) {
        failureCallback(new Error("Zoho ASAP token discarded"));
        return;
      }
      status = STATUS.IDENTIFIED;
      successCallback(token);
    })
    .catch((error) => {
      if (requestSessionId !== sessionId) return;
      if (isExpiredSession(error)) {
        status = STATUS.IDLE;
      } else {
        // 403, 503 or the endpoint not reachable: the help center shows the unavailable page
        status = STATUS.UNAVAILABLE;
        closeIfOpen();
      }
      // Never forward the axios error: its config carries the Manager session token
      failureCallback(new Error("Zoho ASAP token unavailable"));
    });
}

// Drops the click waiting for the widget, without notice, and cancels its deadline
function cancelPendingOpen() {
  if (!pendingOpen) return;
  clearTimeout(pendingOpen.timer);
  pendingOpen = null;
}

// Right after a login or a reload the widget may still be loading: the click waits for it up to
// OPEN_WAIT_MS. A second click while waiting neither queues another opening nor extends the wait
function openWhenReady() {
  if (pendingOpen) return;
  if (typeof window.ZohoDeskAsapReady !== "function") {
    notifyUnavailable();
    return;
  }
  const attempt = { sessionId };
  pendingOpen = attempt;
  attempt.timer = setTimeout(() => {
    if (pendingOpen !== attempt) return;
    pendingOpen = null;
    notifyUnavailable();
  }, OPEN_WAIT_MS);
  whenReady((asap) => {
    // Expired, or dropped by a login or logout: Zoho keeps old callbacks in its queue
    if (pendingOpen !== attempt) return;
    cancelPendingOpen();
    if (attempt.sessionId !== sessionId) return;
    if (status === STATUS.UNAVAILABLE) {
      notifyUnavailable();
      return;
    }
    try {
      asap.invoke("open");
    } catch {
      notifyUnavailable();
    }
  });
}

// Registers what to do when the help center cannot be shown (the Manager routes to its own page)
export function setUnavailableHandler(handler) {
  unavailableHandler = handler;
}

// Called once from the app root, before any session: binds the widget events and hides the
// floating icon, which the Zoho app settings show by default
export function init() {
  whenReady((asap) => {
    bindListeners(asap);
    hideLauncher(asap);
  });
}

// Identifies the authenticated user in Zoho. Called once the user and its profile are loaded, on
// every login and on every reload with an active session
export function identifyUser() {
  if (!isEnabled()) return;
  sessionId++;
  cancelPendingOpen();
  status = STATUS.PENDING;
  whenReady((asap) => {
    bindListeners(asap);
    hideLauncher(asap);
    asap.invoke("login", getJwtToken);
  });
}

// Closes the Zoho session of the user leaving the Manager, so the next one in the same tab never
// sees it. A failure here must not block the Manager logout
export function logout() {
  if (!isEnabled()) return;
  sessionId++;
  cancelPendingOpen();
  status = STATUS.IDLE;
  whenReady((asap) => {
    asap.invoke("close");
    asap.invoke("logout");
    hideLauncher(asap);
  });
}

// Opens the help center, or the unavailable page when the user has no identity in Zoho or the
// integration is off in this environment
export function openHelpCenter() {
  if (!isEnabled() || status === STATUS.UNAVAILABLE) {
    notifyUnavailable();
    return;
  }
  if (!isWidgetReady()) {
    openWhenReady();
    return;
  }
  cancelPendingOpen();
  try {
    window.ZohoDeskAsap.invoke("open");
  } catch {
    notifyUnavailable();
  }
}

// Single sign-on of the help center web portal, opened outside the Manager. Zoho sends the user to
// the Manager's portal login page with the page to come back to; this asks for a new token and
// hands it to Zoho through the portal JWT sign-in URL. The token travels in that URL by Zoho's
// design: it is short-lived and the page is left with location.replace, out of the history
export function redirectToHelpCenterPortal(returnTo) {
  if (!isEnabled() || !PORTAL_SIGNIN_URL) {
    notifyUnavailable();
    return Promise.resolve();
  }
  return requestToken(SSO_JWT_URL)
    .then((token) => {
      if (!token) throw new Error("Zoho portal token unavailable");
      const params = new URLSearchParams({
        return_to: safePortalReturnTo(returnTo),
        jwt: token,
      });
      window.location.replace(`${PORTAL_SIGNIN_URL}?${params.toString()}`);
    })
    .catch((error) => {
      if (!isExpiredSession(error)) notifyUnavailable();
    });
}

export function getStatus() {
  return status;
}

export default {
  init,
  identifyUser,
  logout,
  openHelpCenter,
  redirectToHelpCenterPortal,
  setUnavailableHandler,
  getStatus,
};
